import React, { useContext, useMemo, useState } from 'react';
import { DocumentReviewDialog } from '../DocumentReviewDialog';
import requestService from 'store/request/service';
import documentRequestCandidateService from 'store/document-request-candidate/service';
import { IDocument } from 'common/document/interfaces/document';
import { DocumentTypeEnum } from 'common/document/enums/document-type.enum';
import { paths } from 'routes/Dashboard/DriversPage';
import { IRejectReasonDocument } from 'common/request/interfaces/reject-reson-document';
import { useNavigate } from 'react-router-dom';
import { VerificationStatusEnum } from 'common/document/enums/verification-status.enum';
import useSelector from 'hooks/useSelector';
import { userSelector } from 'store/user/selectors';
import { CompanyContext } from 'index';
import {
  DIALOG_TYPES,
  useGlobalDialogContext,
} from 'App/components/Layouts/Dashboard/dialog/GlobalDialog';
import { DocumentDownloadDialog } from '../DocumentDownloadDialog';
import eventService from 'store/event/service';
import { filterDocumentsToReview } from '../Utils/filter-documents-to-review';
import { DocumentRejectionReasonEnum } from 'common/document/enums/document-rejection-reason.enum';
import { buildLinkToCandidateAuth } from 'functions/linkUtils';
import candidateService from 'store/candidate/service';
import { managerDocumentService } from 'store/manager-document';
import { OriginalFileParams } from './types';
import { defaultOriginalFileParams } from './constants';
import { useWebSocketClientMessageSubscriptionV2 } from 'contexts/webSocketClient/useWebSocketClientSubscription';
import { WebSocketClientMessageTypes } from 'contexts/webSocketClient/messageTypes';
import { useLogsManager } from 'integrations/logs';
import { toast } from 'react-toastify';
import { Snackbar } from '../../../../components/Snackbars';
import { toastConfig } from '../../../../config/ui/toastConfig';

export type IUpdateDocumentImage = {
  file: File;
  candidateDocumentId: string;
  fileName: string;
};

type InputProps = {
  openReviewDialog: boolean;
  setOpenReviewDialog: (v: boolean) => void;
  documentRequestId: string;
  selectedCandidateDocumentId?: string;
  setSelectedCandidateDocumentId?: (candidateDocumentId: string) => void;
};

const timeoutms = 20 * 1000;

export const DocumentReviewContainer = ({
  openReviewDialog,
  documentRequestId,
  setOpenReviewDialog,
  selectedCandidateDocumentId,
  setSelectedCandidateDocumentId,
}: InputProps) => {
  const logs = useLogsManager();
  const navigate = useNavigate();
  const user = useSelector(userSelector);
  const company = useContext(CompanyContext);
  const { showDialog } = useGlobalDialogContext();
  const [activeTabDocumentFile, setActiveTabDocumentFile] =
    useState<string>('');
  const [openDownloadModal, setOpenDownloadModal] = useState<boolean>(false);
  const [documentDownloadPdf, setDocumentDownloadPdf] =
    useState<IDocument | null>(null);

  const [originalFileParams, setOriginalFileParams] =
    useState<OriginalFileParams>(defaultOriginalFileParams);

  const documentStatusSubscription = useWebSocketClientMessageSubscriptionV2(
    WebSocketClientMessageTypes.DOT_VERICATION_IMAGE_UPDATED,
    timeoutms
  );

  const [isLoadingVerificationStatus, setIsLoadingVerificationStatus] =
    useState(false);

  const [downloadPdfDocumentEvent] =
    eventService.endpoints.downloadPdfDocument.useMutation();

  const {
    data: documentsToReview,
    isFetching: isFetchingDocumentForReview,
    refetch: refetchDocumentsToReview,
  } = requestService.endpoints.getDocumentsToReview.useQuery(
    { documentRequestId },
    {
      skip: !documentRequestId,
    }
  );

  const [approveDocumentRequest] =
    requestService.endpoints.approveDocumentRequest.useMutation();

  const [markDriverLicenseAsWithoutPhoto] =
    requestService.endpoints.markDriverLicenseAsWithoutPhoto.useMutation();

  const [
    updateDocumentDetailsDocumentDotMedical,
    { isLoading: isLoadingUpdateDocumentDetailsDocumentDotMedical },
  ] =
    requestService.endpoints.updateDocumentDetailsDocumentDotMedical.useMutation();

  const [updateDocumentFile, { isLoading: isLoadingUpdateDocumentFile }] =
    requestService.endpoints.updateDocumentFile.useMutation();

  const [
    saveDocumentPdfToServer,
    { isLoading: isLoadingSaveDocumentPdfToServer },
  ] =
    documentRequestCandidateService.endpoints.saveDocumentPdfToServer.useMutation();

  const {
    data: currentOriginalFile,
    isLoading: isLoadingOriginalFile,
    isFetching: isFetchingOriginalFile,
  } = candidateService.endpoints.getOriginalDocumentFile.useQuery(
    originalFileParams,
    {
      skip:
        !originalFileParams.fileName || !originalFileParams.candidateDocumentId,
    }
  );

  const documents = useMemo(() => {
    if (!documentsToReview?.documents) {
      return [];
    }
    return filterDocumentsToReview(documentsToReview.documents);
  }, [documentsToReview]);

  const { data: photoIdDocument } =
    managerDocumentService.endpoints.getLastDocumentByCandidateIdAndDocumentType.useQuery(
      {
        candidateId: documentsToReview?.candidateId || '',
        documentType: DocumentTypeEnum.PHOTO_ID,
      },
      { skip: !documentsToReview?.candidateId }
    );

  const handleGoToProfile = () => {
    if (openReviewDialog && documentsToReview) {
      navigate(
        paths.DashboardDriversReviewPage.replace(
          ':id',
          documentsToReview.candidateId
        )
      );
    }
  };

  const handleReject = async (
    document: IDocument,
    reason: IRejectReasonDocument
  ) => {
    if (documentsToReview) {
      setOpenReviewDialog(false);
      if (reason.rejectionReason === DocumentRejectionReasonEnum.OTHER) {
        showDialog(DIALOG_TYPES.CUSTOM_REJECTION_GLOBAL_DIALOG, {
          document: document,
          candidateFullName:
            documentsToReview?.candidateFullName || 'Loading...',
          manager: user,
          companyName: company.name,
          link: buildLinkToCandidateAuth(documentsToReview?.candidateCode),
          request: documentsToReview,
        });
      } else {
        showDialog(DIALOG_TYPES.CONFIRM_REJECTION_GLOBAL_DIALOG, {
          reason,
          document: document,
          candidateFullName:
            documentsToReview?.candidateFullName || 'Loading...',
          manager: user,
          companyName: company.name,
          link: buildLinkToCandidateAuth(documentsToReview?.candidateCode),
          request: documentsToReview,
        });
      }
    }
    return;
  };

  const handleApprove = async (document: IDocument) => {
    if (documentsToReview) {
      return approveDocumentRequest({
        documentRequestId: documentsToReview.id,
        candidateDocumentId: document.candidateDocumentId,
        details: document.details,
      });
    }
    return;
  };

  const handleMarkDriverLicenseAsWithoutPhoto = async (
    documentId: string,
    containsUserPhoto: boolean
  ) => {
    await markDriverLicenseAsWithoutPhoto({
      candidateDocumentId: documentId,
      containsUserPhoto,
    });
  };

  const handleUpdate = async (document: IDocument) => {
    if (
      !isLoadingVerificationStatus &&
      documentsToReview &&
      document.documentType === DocumentTypeEnum.DOT_MEDICAL
    ) {
      setActiveTabDocumentFile('verification');

      documentStatusSubscription.subscribe(
        (message: any) => {
          //TODO: Do something is message.status === VerificationStatusEnum.FAILED

          setIsLoadingVerificationStatus(false);
          if (message.status === VerificationStatusEnum.SUCCESS) {
            documentStatusSubscription.unsubscribe();
            refetchDocumentsToReview();
          } else if (message.status === VerificationStatusEnum.FAILED) {
            logs.logWarning('VerificationStatusEnum.FAILED', {
              documentRequestId,
              message,
            });
            documentStatusSubscription.unsubscribe();
          }
        },
        () => {
          logs.logError('Timeout for DOT_VERICATION_IMAGE_UPDATED WS message', {
            documentRequestId,
          });
          setIsLoadingVerificationStatus(false);
          documentStatusSubscription.unsubscribe();
        }
      );

      setIsLoadingVerificationStatus(true);
      return updateDocumentDetailsDocumentDotMedical({
        candidateDocumentId: document.candidateDocumentId,
        details: document.details,
      });
    }
    return;
  };

  const handleDownload = (document: IDocument) => {
    if (document) {
      setDocumentDownloadPdf(document);
      setOpenReviewDialog(false);
      setOpenDownloadModal(true);
    }
  };

  const handlePurge = (document: IDocument) => {
    setOpenReviewDialog(false);
    showDialog(DIALOG_TYPES.CONFIRM_PURGE_DOCUMENT_GLOBAL_DIALOG, {
      document: {
        ...document,
        documentRequestId: document.documentRequestId ?? documentRequestId,
      },
      candidateFullName: documentsToReview?.candidateFullName || '',
    });
  };

  const handleDownloadPdf = (document: IDocument) => {
    if (document?.documentRequestId && document?.candidateDocumentId) {
      downloadPdfDocumentEvent({
        documentRequestId: document.documentRequestId,
        candidateDocumentId: document.candidateDocumentId,
      });
    }
    setOpenDownloadModal(false);
  };

  const handleUpdateDocumentImage = (data: IUpdateDocumentImage) => {
    updateDocumentFile(data);
  };

  const handleOpenDownloadModal = (v: boolean) => {
    setOpenDownloadModal(v);
    setDocumentDownloadPdf(null);
  };

  const handleOpenReviewDialog = (v: boolean) => {
    setOriginalFileParams(defaultOriginalFileParams);
    setOpenReviewDialog(v);
  };

  const handleSaveDocumentPdfToServer = (
    candidateDocumentId: string,
    filename?: string,
    callbackSuccess?: () => void
  ) => {
    saveDocumentPdfToServer({ candidateDocumentId, filename })
      .unwrap()
      .then(() => {
        toast.success(<Snackbar message={'Saved to server'} />, toastConfig);
        callbackSuccess && callbackSuccess();
      });
  };

  return (
    <div>
      <DocumentReviewDialog
        open={openReviewDialog}
        setOpen={handleOpenReviewDialog}
        isLoading={isFetchingDocumentForReview || isLoadingUpdateDocumentFile}
        isLoadingVerificationStatus={
          isLoadingVerificationStatus ||
          isLoadingUpdateDocumentDetailsDocumentDotMedical
        }
        isLoadingOriginalFile={isLoadingOriginalFile || isFetchingOriginalFile}
        candidateFullName={documentsToReview?.candidateFullName || 'Loading...'}
        employeeId={documentsToReview?.employeeId}
        activeTabDocumentFile={activeTabDocumentFile}
        setActiveTabDocumentFile={setActiveTabDocumentFile}
        onGoToProfile={handleGoToProfile}
        onReject={handleReject}
        onApprove={handleApprove}
        onMarkDriverLicenseAsWithoutPhoto={
          handleMarkDriverLicenseAsWithoutPhoto
        }
        onUpdate={handleUpdate}
        onPurge={handlePurge}
        onDownload={handleDownload}
        onUpdateDocumentImage={handleUpdateDocumentImage}
        setOriginalFileParams={setOriginalFileParams}
        currentOriginalFile={currentOriginalFile}
        documents={documents}
        selectedCandidateDocumentId={selectedCandidateDocumentId}
        setSelectedCandidateDocumentId={setSelectedCandidateDocumentId}
      />
      {documentDownloadPdf && (
        <DocumentDownloadDialog
          open={openDownloadModal}
          setOpen={handleOpenDownloadModal}
          driverName={documentsToReview?.candidateFullName || ''}
          employeeId={documentsToReview?.employeeId || ''}
          document={documentDownloadPdf}
          photoIdDocument={photoIdDocument}
          onDownload={handleDownloadPdf}
          saveDocumentPdfToServer={handleSaveDocumentPdfToServer}
          isLoadingSaveDocumentPdfToServer={isLoadingSaveDocumentPdfToServer}
        />
      )}
    </div>
  );
};
